import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { serviceTypesService } from '../../services/logistics/service-types';
import LoadingSpinner from '../ui/LoadingSpinner';
import toast from 'react-hot-toast';
import {
  XMarkIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';

const ServiceTypesModal = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { currentCompany } = useAuth();
  const [loading, setLoading] = useState(false);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [formData, setFormData] = useState({
    name: { en: '', ar: '' },
    value: '',
    color: 'blue'
  });

  const colorOptions = [
    { value: 'blue', label: 'Blue', class: 'bg-blue-500' },
    { value: 'green', label: 'Green', class: 'bg-green-500' },
    { value: 'red', label: 'Red', class: 'bg-red-500' },
    { value: 'yellow', label: 'Yellow', class: 'bg-yellow-500' },
    { value: 'purple', label: 'Purple', class: 'bg-purple-500' },
    { value: 'cyan', label: 'Cyan', class: 'bg-cyan-500' },
    { value: 'gray', label: 'Gray', class: 'bg-gray-500' },
    { value: 'orange', label: 'Orange', class: 'bg-orange-500' },
    { value: 'pink', label: 'Pink', class: 'bg-pink-500' },
    { value: 'indigo', label: 'Indigo', class: 'bg-indigo-500' }
  ];



  useEffect(() => {
    console.log('ServiceTypesModal useEffect - isOpen:', isOpen, 'currentCompany:', currentCompany?.id);
    if (isOpen && currentCompany?.id) {
      loadServiceTypes();
    }
  }, [isOpen, currentCompany?.id]);

  const loadServiceTypes = async () => {
    if (!currentCompany?.id) return;
    
    console.log('Loading service types for company:', currentCompany.id);
    setLoading(true);
    try {
      const types = await serviceTypesService.getServiceTypes(currentCompany.id);
      console.log('Loaded service types:', types);
      setServiceTypes(types);
    } catch (error) {
      console.error('Error loading service types:', error);
      toast.error(t('common.error_loading_data'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field === 'name') {
      setFormData(prev => ({
        ...prev,
        name: { ...prev.name, ...value }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const resetForm = () => {
    setFormData({
      name: { en: '', ar: '' },
      value: '',
      color: 'blue'
    });
    setEditingType(null);
    setShowAddForm(false);
  };

  const clearFormData = () => {
    setFormData({
      name: { en: '', ar: '' },
      value: '',
      color: 'blue'
    });
    setEditingType(null);
  };

  const handleAdd = () => {
    console.log('handleAdd called - setting showAddForm to true');
    setShowAddForm(true);
    clearFormData();
  };

  const handleEdit = (serviceType) => {
    setEditingType(serviceType);
    setFormData({
      name: serviceType.name || { en: '', ar: '' },
      value: serviceType.value || '',
      color: serviceType.color || 'blue'
    });
    setShowAddForm(true);
  };

  const handleDelete = async (serviceType) => {
    if (!window.confirm(t('service_types.confirm_delete', 'Are you sure you want to delete this service type?'))) {
      return;
    }

    try {
      setLoading(true);
      
      if (serviceType.isDefault) {
        // For default types, convert to custom first, then delete
        await serviceTypesService.deleteDefaultServiceType(serviceType.value, currentCompany.id);
        toast.success(t('service_types.deleted_successfully'));
      } else {
        await serviceTypesService.deleteServiceType(serviceType.id);
        toast.success(t('service_types.deleted_successfully'));
      }
      
      loadServiceTypes();
    } catch (error) {
      console.error('Error deleting service type:', error);
      toast.error(t('common.delete_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.en || !formData.name.ar || !formData.value) {
      toast.error(t('service_types.fill_all_fields', 'Please fill all required fields'));
      return;
    }

    try {
      setLoading(true);
      
      const serviceTypeData = {
        ...formData,
        companyId: currentCompany.id
      };

      if (editingType) {
        if (editingType.isDefault) {
          // For default types, save as custom first
          await serviceTypesService.saveDefaultAsCustom(editingType, currentCompany.id);
        }
        await serviceTypesService.updateServiceType(editingType.id, serviceTypeData);
        toast.success(t('service_types.updated_successfully'));
      } else {
        await serviceTypesService.addServiceType(serviceTypeData);
        toast.success(t('service_types.added_successfully'));
      }

      clearFormData();
      setShowAddForm(false);
      loadServiceTypes();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error saving service type:', error);
      toast.error(t('common.save_failed'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3 rtl:space-x-reverse">
            <Cog6ToothIcon className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              {t('service_types.manage_service_types', 'Manage Service Types')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="flex h-[calc(90vh-120px)]">
          {/* Service Types List */}
          <div className="w-1/2 border-r overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {t('service_types.service_types', 'Service Types')}
                </h3>
                <button
                  onClick={handleAdd}
                  className="btn btn-primary btn-sm"
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  {t('service_types.add_new', 'Add New')}
                </button>
              </div>

              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <LoadingSpinner />
                </div>
              ) : (
                <div className="space-y-2">
                  {serviceTypes.map((serviceType) => (
                    <div
                      key={serviceType.id || serviceType.value}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center space-x-3 rtl:space-x-reverse">
                        <div>
                          <div className="font-medium">
                            {serviceType.name?.en || serviceType.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {serviceType.name?.ar}
                          </div>
                          <div className="text-xs text-gray-400 font-mono">
                            {serviceType.value}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 rtl:space-x-reverse">
                        {serviceType.isDefault && (
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                            {t('service_types.default', 'Default')}
                          </span>
                        )}
                        <button
                          onClick={() => handleEdit(serviceType)}
                          className="btn btn-ghost btn-xs"
                          title={t('common.edit')}
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(serviceType)}
                          className="btn btn-ghost btn-xs text-red-600"
                          title={t('common.delete')}
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Add/Edit Form */}
          <div className="w-1/2 overflow-y-auto">
            {(() => {
              console.log('Rendering form section - showAddForm:', showAddForm);
              return showAddForm;
            })() ? (
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {editingType ? t('service_types.edit_service_type', 'Edit Service Type') : t('service_types.add_service_type', 'Add Service Type')}
                </h3>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* English Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('service_types.name_en', 'Name (English)')} *
                    </label>
                    <input
                      type="text"
                      value={formData.name.en}
                      onChange={(e) => handleInputChange('name', { en: e.target.value })}
                      className="input input-bordered w-full"
                      required
                    />
                  </div>

                  {/* Arabic Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('service_types.name_ar', 'Name (Arabic)')} *
                    </label>
                    <input
                      type="text"
                      value={formData.name.ar}
                      onChange={(e) => handleInputChange('name', { ar: e.target.value })}
                      className="input input-bordered w-full"
                      required
                    />
                  </div>

                  {/* Value */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('service_types.value', 'Value')} *
                    </label>
                    <input
                      type="text"
                      value={formData.value}
                      onChange={(e) => handleInputChange('value', e.target.value)}
                      className="input input-bordered w-full"
                      placeholder="e.g., express, premium"
                      required
                    />
                  </div>



                  {/* Color */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('service_types.color', 'Color')}
                    </label>
                    <div className="grid grid-cols-5 gap-2">
                      {colorOptions.map((color) => (
                        <button
                          key={color.value}
                          type="button"
                          onClick={() => handleInputChange('color', color.value)}
                          className={`p-3 rounded border-2 transition-colors ${
                            formData.color === color.value
                              ? 'border-gray-800'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className={`w-4 h-4 rounded ${color.class}`}></div>
                          <div className="text-xs mt-1">{color.label}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-end space-x-3 rtl:space-x-reverse pt-4">
                    <button
                      type="button"
                      onClick={resetForm}
                      className="btn btn-outline"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={loading}
                    >
                      {loading ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        editingType ? t('common.update') : t('common.add')
                      )}
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                <div className="text-center">
                  <Cog6ToothIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>{t('service_types.select_to_edit', 'Select a service type to edit or add a new one')}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceTypesModal; 